import { emit } from './emit'
import { deltaMs, readNumber, safeSessionGet, safeSessionRemove, safeSessionSet, secondsSince } from './sessionStore'
import { newUuid } from './uuid'

/**
 * OTP funnel analytics helpers.
 *
 * The patient OTP flow is entirely client-side (Next.js → AWS Cognito CUSTOM_AUTH
 * → Twilio in a Lambda), so all funnel telemetry is emitted from the browser.
 *
 * A single OTP "flow" is identified by `otp_session_id`, a UUID generated on the
 * first `otp_send_requested` and reused across resends and submit attempts until
 * the flow ends (verified or abandoned). It lets the warehouse correlate events
 * for one user/one phone-entry without relying on Segment's anonymous id, which
 * does not reset between abandoned-and-restarted flows.
 *
 * Privacy: never include `phoneNumber`, `otpCode`, JWT material, or any other
 * Cognito session bytes on these events. The route handlers in `app/auth/*`
 * already enforce this on the server side; mirror the same rule here.
 */

export type OtpFlow = 'login' | 'signup'

export type OtpEventName =
    | 'otp_send_requested'
    | 'otp_send_completed'
    | 'otp_screen_viewed'
    | 'otp_first_digit_entered'
    | 'otp_resend_requested'
    | 'otp_submit_attempted'
    | 'otp_verified'
    | 'otp_screen_abandoned'

export type OtpSendResult =
    | 'success'
    | 'unknown_user'
    | 'rate_limited'
    | 'network_error'
    | 'unknown_error'

export type OtpSubmitResult =
    | 'success'
    | 'invalid_code'
    | 'expired_session'
    | 'rate_limited'
    | 'network_error'
    | 'unknown_error'

const SESSION_ID_KEY = 'otpSessionId'
const SEND_REQUESTED_AT_KEY = 'otpSendRequestedAt'
const SEND_COMPLETED_AT_KEY = 'otpSendCompletedAt'
const SCREEN_VIEWED_AT_KEY = 'otpScreenViewedAt'
const FIRST_DIGIT_AT_KEY = 'otpFirstDigitAt'
const RESEND_COUNT_KEY = 'otpResendCount'

/**
 * Returns the current OTP session id, generating one if none exists.
 * Should be called at the moment the user clicks "Continue" on the phone entry screen.
 */
export function ensureOtpSessionId(): string {
    const existing = safeSessionGet(SESSION_ID_KEY)
    if (existing) return existing
    const id = newUuid()
    safeSessionSet(SESSION_ID_KEY, id)
    return id
}

export function getOtpSessionId(): string | null {
    return safeSessionGet(SESSION_ID_KEY)
}

/**
 * Clears all OTP-flow scoped storage. Call after `otp_verified` or `otp_screen_abandoned`.
 */
export function clearOtpSession(): void {
    safeSessionRemove(SESSION_ID_KEY)
    safeSessionRemove(SEND_REQUESTED_AT_KEY)
    safeSessionRemove(SEND_COMPLETED_AT_KEY)
    safeSessionRemove(SCREEN_VIEWED_AT_KEY)
    safeSessionRemove(FIRST_DIGIT_AT_KEY)
    safeSessionRemove(RESEND_COUNT_KEY)
}

export function getResendCount(): number {
    return readNumber(RESEND_COUNT_KEY) ?? 0
}

function bumpResendCount(): number {
    const next = getResendCount() + 1
    safeSessionSet(RESEND_COUNT_KEY, String(next))
    return next
}

function emitOtp(eventName: OtpEventName, properties: Record<string, unknown>): void {
    emit(eventName, properties)
}

export function trackOtpSendRequested(args: {
    flow: OtpFlow
    isResend?: boolean
}): { otpSessionId: string; sendRequestedAt: number } {
    const sessionId = ensureOtpSessionId()
    const now = Date.now()
    safeSessionSet(SEND_REQUESTED_AT_KEY, String(now))

    emitOtp('otp_send_requested', {
        flow: args.flow,
        otp_session_id: sessionId,
        is_resend: !!args.isResend,
    })

    return { otpSessionId: sessionId, sendRequestedAt: now }
}

export function trackOtpSendCompleted(args: {
    flow: OtpFlow
    result: OtpSendResult
    statusCode?: number
    durationMs: number
    isResend?: boolean
}): void {
    const sessionId = getOtpSessionId()
    if (args.result === 'success') {
        safeSessionSet(SEND_COMPLETED_AT_KEY, String(Date.now()))
    }

    emitOtp('otp_send_completed', {
        flow: args.flow,
        otp_session_id: sessionId,
        result: args.result,
        status_code: args.statusCode,
        duration_ms: Math.max(0, Math.round(args.durationMs)),
        is_resend: !!args.isResend,
    })
}

export function trackOtpScreenViewed(args: {
    flow: OtpFlow
    isExistingUser: boolean
}): void {
    safeSessionSet(SCREEN_VIEWED_AT_KEY, String(Date.now()))

    emitOtp('otp_screen_viewed', {
        flow: args.flow,
        otp_session_id: getOtpSessionId(),
        is_existing_user: args.isExistingUser,
    })
}

/**
 * Fires once per send/resend. The caller is responsible for resetting the
 * "first digit fired" flag in component state when they trigger a resend.
 */
export function trackOtpFirstDigitEntered(args: { flow: OtpFlow }): void {
    if (safeSessionGet(FIRST_DIGIT_AT_KEY)) return
    safeSessionSet(FIRST_DIGIT_AT_KEY, String(Date.now()))

    emitOtp('otp_first_digit_entered', {
        flow: args.flow,
        otp_session_id: getOtpSessionId(),
        time_to_first_digit_ms: deltaMs(SEND_COMPLETED_AT_KEY) ?? deltaMs(SEND_REQUESTED_AT_KEY),
        resend_count: getResendCount(),
    })
}

export function clearFirstDigitMark(): void {
    safeSessionRemove(FIRST_DIGIT_AT_KEY)
}

export function trackOtpResendRequested(args: {
    flow: OtpFlow
    digitsEnteredBeforeResend: number
}): { resendCount: number } {
    const resendCount = bumpResendCount()
    clearFirstDigitMark()

    emitOtp('otp_resend_requested', {
        flow: args.flow,
        otp_session_id: getOtpSessionId(),
        resend_count: resendCount,
        seconds_since_initial_send: secondsSince(SEND_REQUESTED_AT_KEY),
        digits_entered_before_resend: args.digitsEnteredBeforeResend,
    })

    return { resendCount }
}

export function trackOtpSubmitAttempted(args: {
    flow: OtpFlow
    attemptNumber: number
    result: OtpSubmitResult
    statusCode?: number
    verifyDurationMs: number
}): void {
    emitOtp('otp_submit_attempted', {
        flow: args.flow,
        otp_session_id: getOtpSessionId(),
        attempt_number: args.attemptNumber,
        result: args.result,
        status_code: args.statusCode,
        verify_duration_ms: Math.max(0, Math.round(args.verifyDurationMs)),
        time_to_submit_ms: deltaMs(SCREEN_VIEWED_AT_KEY),
        time_from_first_digit_ms: deltaMs(FIRST_DIGIT_AT_KEY),
        resend_count: getResendCount(),
    })
}

export function trackOtpVerified(args: {
    flow: OtpFlow
    totalAttempts: number
    isExistingUser: boolean
}): void {
    emitOtp('otp_verified', {
        flow: args.flow,
        otp_session_id: getOtpSessionId(),
        total_duration_ms: deltaMs(SEND_REQUESTED_AT_KEY),
        total_attempts: args.totalAttempts,
        total_resends: getResendCount(),
        is_existing_user: args.isExistingUser,
    })
}

export function trackOtpScreenAbandoned(args: {
    flow: OtpFlow
    digitsEntered: number
    didResend: boolean
    attemptCount: number
}): void {
    emitOtp('otp_screen_abandoned', {
        flow: args.flow,
        otp_session_id: getOtpSessionId(),
        digits_entered: args.digitsEntered,
        did_resend: args.didResend,
        did_submit: args.attemptCount > 0,
        attempt_count: args.attemptCount,
        seconds_on_screen: secondsSince(SCREEN_VIEWED_AT_KEY),
    })
}

/**
 * Coarse classification of an `/auth/initiate` response into our event taxonomy.
 * - 2xx → success
 * - 409 → unknown_user (login attempt for a phone Cognito has no record of)
 * - 429 → rate_limited
 * - everything else → unknown_error
 */
export function classifyInitiateResponse(status: number): OtpSendResult {
    if (status >= 200 && status < 300) return 'success'
    if (status === 409) return 'unknown_user'
    if (status === 429) return 'rate_limited'
    return 'unknown_error'
}

/**
 * Coarse classification of an `/auth/login` response.
 */
export function classifyLoginResponse(status: number): OtpSubmitResult {
    if (status >= 200 && status < 300) return 'success'
    if (status === 401) return 'invalid_code'
    if (status === 419 || status === 440) return 'expired_session'
    if (status === 429) return 'rate_limited'
    return 'unknown_error'
}
